/**
 * Builds the breadcrumb from the shopper's path in this tab.
 * A product opened from a collection, including that page's "you may also like"
 * carousel, keeps that collection. Search and PDP "you may also like" stay
 * Home / product.
 */
(() => {
  const STORAGE_KEY = 'mg-bc-trail';

  const productHandle = (pathname) => {
    const match = String(pathname || '').match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const collectionHandleFromProductPath = (pathname) => {
    const match = String(pathname || '').match(/\/collections\/([^/?#]+)\/products\//);
    return match ? decodeURIComponent(match[1]) : '';
  };

  const safeUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      return `${url.pathname}${url.search}`;
    } catch {
      return '';
    }
  };

  const readTrail = () => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeTrail = (trail) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
    } catch {
      /* sessionStorage can be unavailable in private mode */
    }
  };

  const rememberHistory = (trail) => {
    try {
      const state = history.state && typeof history.state === 'object' ? history.state : {};
      history.replaceState({ ...state, mgBcTrail: trail }, '');
    } catch {
      /* ignore non-cloneable history state */
    }
  };

  const navigationType = () => {
    const entry = performance.getEntriesByType('navigation')[0];
    return entry && entry.type ? entry.type : 'navigate';
  };

  const breadcrumbNav = () => document.querySelector('nav[data-mg-breadcrumb]');

  const homeCrumb = (nav) => {
    const link = nav?.querySelector('.mg-breadcrumb__link');
    if (link) {
      return {
        type: 'home',
        label: link.textContent.trim() || 'Home',
        url: link.getAttribute('href') || document.body.dataset.mgRootUrl || '/',
      };
    }

    return {
      type: 'home',
      label: document.body.dataset.mgHomeLabel || 'Home',
      url: document.body.dataset.mgRootUrl || '/',
    };
  };

  const pageCrumb = (nav) => {
    if (!nav) return null;
    const type = nav.dataset.mgPageType || '';
    if (!type) return null;
    const current = nav.querySelector('.mg-breadcrumb__current');
    return {
      type,
      label: (nav.dataset.mgPageLabel || current?.textContent || '').trim(),
      url: nav.dataset.mgPageUrl || window.location.pathname,
    };
  };

  const collectionCrumb = (label, url) => {
    const safe = safeUrl(url);
    const text = String(label || '').trim().slice(0, 120);
    if (!safe || !text) return null;
    return { type: 'collection', label: text, url: safe };
  };

  const renderAncestors = (nav, trail) => {
    const list = nav.querySelector('.mg-breadcrumb__list');
    const current = list?.querySelector('.mg-breadcrumb__item--current');
    if (!list || !current) return;

    list.querySelectorAll('[data-mg-bc-ancestor]').forEach((node) => node.remove());

    trail
      .filter((item) => item && item.type === 'collection')
      .forEach((item) => {
        const crumb = collectionCrumb(item.label, item.url);
        if (!crumb) return;

        const itemEl = document.createElement('li');
        itemEl.className = 'mg-breadcrumb__item';
        itemEl.dataset.mgBcAncestor = 'true';

        const link = document.createElement('a');
        link.className = 'mg-breadcrumb__link';
        link.href = crumb.url;
        if (crumb.label.endsWith('.')) link.dataset.hasPeriod = 'true';
        link.textContent = crumb.label;

        itemEl.append(link);
        list.insertBefore(itemEl, current);
      });
  };

  const apply = () => {
    const nav = breadcrumbNav();
    const page = pageCrumb(nav);
    if (!nav || !page) return;

    const home = homeCrumb(nav);

    if (page.type === 'collection') {
      const trail = [home, { type: 'collection', label: page.label, url: safeUrl(page.url) || page.url }];
      writeTrail(trail);
      rememberHistory(trail);
      return;
    }

    if (page.type !== 'product') return;

    const product = {
      type: 'product',
      label: page.label,
      url: safeUrl(page.url) || window.location.pathname,
    };
    const currentHandle = productHandle(window.location.pathname) || productHandle(product.url);

    if (navigationType() === 'back_forward' && Array.isArray(history.state?.mgBcTrail)) {
      const stored = history.state.mgBcTrail;
      const last = stored[stored.length - 1];
      const sameProduct =
        last?.type === 'product' &&
        (!productHandle(last.url) || productHandle(last.url) === currentHandle);
      if (sameProduct) {
        const trail = stored.map((item, index) =>
          index === stored.length - 1 ? { ...product } : item
        );
        writeTrail(trail);
        renderAncestors(nav, trail);
        return;
      }
    }

    const stored = readTrail();
    const last = stored[stored.length - 1];
    const urlCollection = collectionHandleFromProductPath(window.location.pathname);
    let trail;

    if (last?.type === 'product' && productHandle(last.url) === currentHandle) {
      trail = stored.slice(0, -1).concat(product);
    } else if (urlCollection) {
      const known = stored.find(
        (item) =>
          item?.type === 'collection' &&
          String(item.url || '').includes(`/collections/${urlCollection}`)
      );
      const collection =
        known ||
        collectionCrumb(
          urlCollection.replace(/-/g, ' '),
          window.location.pathname.replace(/\/products\/[^/]+\/?$/, '')
        );
      trail = collection ? [home, collection, product] : [home, product];
    } else {
      trail = [home, product];
    }

    writeTrail(trail);
    rememberHistory(trail);
    renderAncestors(nav, trail);
  };

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link) return;

      const destination = safeUrl(link.getAttribute('href'));
      const handle = productHandle(destination);
      if (!handle) return;

      const nav = breadcrumbNav();
      const page = pageCrumb(nav);
      const ancestors = [homeCrumb(nav)];
      const template = document.querySelector('main')?.dataset.template || '';
      const fromSearch =
        template === 'search' ||
        template.startsWith('search.') ||
        Boolean(link.closest('#nosto-serp, #nosto-autocomplete, predictive-search-component, .predictive-search, mg-header-search'));
      const fromPdpAlsoLike =
        page?.type === 'product' &&
        Boolean(link.closest('#product-nosto-also-like, .product-recommendations'));

      if (!fromSearch && !fromPdpAlsoLike && page?.type === 'collection') {
        const currentCollection = collectionCrumb(page.label, page.url);
        if (currentCollection) ancestors.push(currentCollection);
      } else if (!fromSearch && !fromPdpAlsoLike && page?.type === 'product') {
        const previous = readTrail().find((item) => item?.type === 'collection' && item.label && item.url);
        if (previous) ancestors.push(previous);
      }

      ancestors.push({ type: 'product', label: '', url: destination });
      writeTrail(ancestors);
    },
    true
  );

  window.mgBreadcrumbApply = apply;
})();
